import type { ReactNode } from 'react';
import Button from '../Button';
import Dialog from '../Dialog/Dialog';
import DialogActions from '../Dialog/DialogActions';
import DialogContent from '../Dialog/DialogContent';
import DialogTitle from '../Dialog/DialogTitle';

export type ConfirmationDialogVariant = 'primary' | 'warning' | 'danger';

export type ConfirmationDialogProps = {
  open: boolean;
  onClose: () => void;
  onConfirm: () => void;

  title: ReactNode;

  /** Body text or arbitrary content (radio groups, detail rows, etc.) */
  children?: ReactNode;

  /**
   * Optional banner rendered below the body — useful for "Will notify" copy
   * or conflict warnings. Consumers supply the full styled node.
   */
  notice?: ReactNode;

  confirmLabel?: string;    // default "Confirm"
  cancelLabel?: string;     // default "Cancel"

  /**
   * primary  — dark green (default, scheduling confirms)
   * warning  — amber (conflict overrides, proceed-anyway)
   * danger   — red (destructive actions)
   */
  confirmVariant?: ConfirmationDialogVariant;
  confirmDisabled?: boolean;
};

const CONFIRM_COLOR: Record<ConfirmationDialogVariant, 'primary' | 'warning' | 'error'> = {
  primary: 'primary',
  warning: 'warning',
  danger:  'error',
};

export function ConfirmationDialog({
  open,
  onClose,
  onConfirm,
  title,
  children,
  notice,
  confirmLabel = 'Confirm',
  cancelLabel = 'Cancel',
  confirmVariant = 'primary',
  confirmDisabled = false,
}: ConfirmationDialogProps) {
  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle sx={{ p: '20px 24px 12px', fontSize: 15, fontWeight: 600 }}>
        {title}
      </DialogTitle>

      <DialogContent sx={{ p: '0 24px 20px', display: 'flex', flexDirection: 'column', gap: '16px' }}>
        {children}
        {notice}
      </DialogContent>

      <DialogActions sx={{ p: '12px 24px', gap: '8px', justifyContent: 'flex-end' }}>
        <Button color="gray" variant="outlined" size="small" onClick={onClose}>
          {cancelLabel}
        </Button>
        <Button
          color={CONFIRM_COLOR[confirmVariant]}
          variant="contained"
          size="small"
          disabled={confirmDisabled}
          onClick={onConfirm}
        >
          {confirmLabel}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
